import React, { useEffect, useState, useRef } from 'react';
import Button from '../../../components/controls/button';
import Style from './SetupMfa.module.css';
import HanshiWhiteLogo from '../../../assets/hanshi/full-white-sm.svg';
import { useHistory } from "react-router";
import { useDispatch } from 'react-redux';
import { generalActions } from '../../../store/actions';
import { mfaService } from '../../../store/services/mfa.service';
import PinInput from "react-pin-input";
import { QRCodeSVG } from 'qrcode.react';
import { Check, Lock } from "lucide-react";

const SetupMfa = (props) => {
  const {
    isMfaSetupRequired,
    mfaSetupToken,
    resetLoginActionsRequest
  } = props;

  const history = useHistory();
  const dispatch = useDispatch();

  const [showTimedCheckMark, setShowTimedCheckMark] = useState(false);
  const [mfaEnrollment, setMfaEnrollment] = useState({
    step: 'initial', // 'initial', 'enrolling', 'completed', 'finished'
    qrCodeUri: '',
    manualEntryKey: '',
    backupCodes: [],
    loading: false,
    error: ''
  });
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState('');
  const [isValidEnrollCode, setIsValidEnrollCode] = useState(false);
  const enrollPinCodeRef = useRef();

  useEffect(() => {
    if (!isMfaSetupRequired || !mfaSetupToken) {
      history.push('/');
    }
  }, [isMfaSetupRequired, mfaSetupToken]);

  const activateTimedCheckMark = () => {
    setShowTimedCheckMark(true);
    setTimeout(() => {
      setShowTimedCheckMark(false);
    }, 500);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleMfaEnroll = async () => {
    setMfaEnrollment({ ...mfaEnrollment, loading: true, error: '' });
    const result = await mfaService.enroll(mfaSetupToken);
    if (result.success) {
      setMfaEnrollment({
        ...mfaEnrollment,
        step: 'enrolling',
        qrCodeUri: result.data.qrCodeUri,
        manualEntryKey: result.data.manualEntryKey,
        loading: false
      });
    } else {
      setMfaEnrollment({
        ...mfaEnrollment,
        loading: false,
        error: result.error
      });
    }
  };

  const handleEnrollCodeChanged = (code) => {
    const isValid = /^\d{6}$/.test(code);
    setIsValidEnrollCode(isValid);
  };

  const handleMfaVerify = async () => {
    setMfaEnrollment({ ...mfaEnrollment, loading: true });
    const result = await mfaService.validate(mfaCode, mfaSetupToken);
    if (result.success) {
      resetLoginActionsRequest();
      setMfaEnrollment({
        ...mfaEnrollment,
        step: 'completed',
        backupCodes: result.data.backupCodes,
        loading: false
      });
      setMfaCode('');
      setMfaError('');
      setIsValidEnrollCode(false);
      dispatch(
        generalActions.showNotificationToast({
          title: "MFA has been successfully enabled for your account",
          type: "success",
        })
      );
    } else {
      setMfaEnrollment({ ...mfaEnrollment, loading: false });
      setMfaError(result.error);
      setMfaCode('');
      setIsValidEnrollCode(false);
      enrollPinCodeRef?.current?.clear();
      enrollPinCodeRef?.current?.focus();
    }
  };

  const markEnrollmentStepAsFinished = () => {
    setMfaEnrollment({ ...mfaEnrollment, step: 'finished' });
    history.push('/');
  };

  return (
    <div className={Style.mainWrapper}>
      <div className={Style.card}>
        <div className="w-full flex justify-center">
          <img src={HanshiWhiteLogo} alt="Hanshi Logo" className={Style.logo} />
        </div>
        <div className={Style.cardContent}>
          <div className={Style.cardTitleContainer}>
            <p className={`${Style.cardTitle} text-xl-700`}>
              {mfaEnrollment.step === 'initial' ?
              'Multi-factor authentication required':
              'Multi-factor authentication setup'}
            </p>
            <p className={`${Style.cardDescription} text-sm-500`}>
              {mfaEnrollment.step === 'initial' && 
                "Your organization requires multi-factor authentication. Set it up now to finish signing in."
              }
              {mfaEnrollment.step === 'enrolling' && 
                "Follow the steps below to complete your MFA setup."
              }
              {mfaEnrollment.step === 'completed' && 
                "Your account is now secured with MFA. Save your backup codes in a safe place."
              }
            </p>
          </div>

          {mfaEnrollment.step === 'initial' && (
            <>
              {mfaEnrollment.error && (
                <p className={Style.errorMessage}>{mfaEnrollment.error}</p>
              )}
            </>
          )}

          {mfaEnrollment.step === 'enrolling' && (
            <div className="space-y-6">
              <div>
                <p className={`${Style.cardTitle} text-base`}>Step 1: Scan QR Code</p>
                <p className={`${Style.cardDescription} text-sm-500 mb-4`}>
                  Scan this QR code with your authenticator app (Microsoft Authenticator, Authy, etc.)
                </p>
                <div className="flex justify-center mb-4">
                  <div className="bg-white p-4 rounded-lg">
                    <QRCodeSVG 
                      value={mfaEnrollment.qrCodeUri}
                      size={200}
                      bgColor="#ffffff"
                      fgColor="#000000"
                      level="M"
                      imageSettings={{
                        src: "https://ppmg-dev.s3.ca-central-1.amazonaws.com/logos/default.svg",
                        x: undefined,
                        y: undefined,
                        height: 24,
                        width: 120,
                        opacity: 1,
                        excavate: true,
                      }}
                    />
                  </div>
                </div>
              </div>

              <div>
                <p className={`${Style.cardTitle} text-base mb-2`}>Or enter this key manually:</p>
                <div className="flex row gap-2 items-center">
                  <code className="flex-1 text-center bg-gray-800 text-white px-3 py-2 rounded text-sm break-all">
                    {mfaEnrollment.manualEntryKey}
                  </code>
                  <Button 
                    btnClass="btn_w100 btn_w100_secondary"
                    label={showTimedCheckMark ? "Copied!" : "Copy"}
                    clickHandler={() => {
                      copyToClipboard(mfaEnrollment.manualEntryKey);
                      activateTimedCheckMark();
                    }} 
                  />
                </div>
              </div>

              <div>
                <p className={`${Style.cardTitle} text-base mb-2`}>Step 2: Enter Verification Code</p>
                <div className="flex flex-col items-center gap-4">
                  <PinInput
                    ref={enrollPinCodeRef}
                    length={6}
                    type="numeric" 
                    inputMode="number"
                    inputStyle={{ 
                      borderColor: '#4a5568', 
                      borderRadius: '4px', 
                      color: '#ffffff',
                      backgroundColor: '#1a1f2e'
                    }}
                    inputFocusStyle={{ borderColor: '#6366f1' }}
                    autoSelect={true}
                    focus={true}
                    onComplete={(val) => { setMfaCode(val); }}
                    onChange={(val) => { handleEnrollCodeChanged(val); }} 
                  />
                  {mfaError && (
                    <p className={Style.errorMessage}>{mfaError}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {mfaEnrollment.step === 'completed' && (
            <div className="space-y-4">
              <div className="flex items-center justify-center gap-2 text-green-500 mb-4">
                <Check size={24} />
                <p className="text-lg font-semibold">MFA enabled successfully</p>
              </div>

              <div>
                <p className={`${Style.cardTitle} text-base mb-2`}>Save Your Backup Codes</p>
                <p className={`${Style.cardDescription} text-sm-500 mb-4`}>
                  Store these backup codes in a safe place. You can use them to access your account if you lose your authenticator device.
                </p>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {mfaEnrollment.backupCodes.map((code, index) => (
                    <code key={index} className="bg-gray-800 text-white px-3 py-2 rounded text-sm text-center">
                      {code}
                    </code>
                  ))}
                </div>
                <div className="flex gap-2 justify-center">
                  <Button 
                    btnClass="btn_w100 btn_w100_secondary"
                    label={showTimedCheckMark ? "Copied!" : "Copy All Codes"}
                    clickHandler={() => {
                      copyToClipboard(mfaEnrollment.backupCodes.join('\n')); 
                      activateTimedCheckMark();
                    }} 
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className={Style.btnWrapper}>
          {mfaEnrollment.step === 'initial' && (
            <Button
              btnClass="btn_w100 btn_w100_primary"
              label="Start Setup"
              clickHandler={handleMfaEnroll}
              showLoading={mfaEnrollment.loading}
              isDisabled={mfaEnrollment.loading} 
            />
          )}
          {mfaEnrollment.step === 'enrolling' && isValidEnrollCode && (
            <Button
              btnClass="btn_w100 btn_w100_primary"
              label="Verify & Enable"
              clickHandler={handleMfaVerify}
              showLoading={mfaEnrollment.loading}
              isDisabled={mfaEnrollment.loading || !isValidEnrollCode} 
            />
          )}
          {mfaEnrollment.step === 'completed' && (
            <Button
              btnClass="btn_w100 btn_w100_primary"
              label="Proceed"
              clickHandler={markEnrollmentStepAsFinished}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SetupMfa;